import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Data-access service backed by the backend `/db` REST endpoints.
 *
 * These 10 endpoints replace the frontend's former direct Supabase table
 * access. Every request is scoped to the authenticated user on the backend
 * (the JWT `sub`); the client never sends its own `user_id`. The bearer token
 * is attached automatically by `AuthInterceptor` for any request to
 * `environment.backendUrl`.
 *
 * Tables covered: `repositories`, `ui_histories`, `internal_histories`,
 * `usdm_sessions`, `usdm_state_histories`.
 */

// --------------------------------------------------------------------------- //
// DTOs (mirror app/schemas/db.py on the backend)
// --------------------------------------------------------------------------- //

/** A single repository (workspace) row — `GET /db/repo`, `GET /db/repo/{id}`. */
export interface RepoOut {
  id: string;
  name: string;
  type: string;
  created_at: string;
}

/** Summary row for the chat/session list — `GET /db/uihistory`. */
export interface ChatOut {
  id: string;
  session_id: string;
  label: string | null;
  created_at: string;
}

/** Full UI/internal history record for a session. */
export interface HistoryOut {
  id: string;
  session_id: string;
  label: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  history: any;
  repo_id: string;
  user_id: string;
  created_at: string;
}

/** Create-or-replace payload for a UI/internal history (keyed by session_id). */
export interface HistoryUpsertIn {
  session_id: string;
  repo_id: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  history: any;
  label?: string | null;
}

/** A USDM authoring session — `GET /db/usdm/sessions`. */
export interface USDMSessionOut {
  id: string;
  session_id: string;
  user_id: string;
  repo_id: string | null;
  osb_study_id: string | null;
}

/** Patch payload for a USDM session — `PATCH /db/usdm/sessions/{session_id}`. */
export interface USDMSessionUpdateIn {
  osb_study_id?: string | null;
}

/** A per-state history row for a USDM session. */
export interface USDMStateHistoryOut {
  id: string;
  session_id: string;
  state: string;
  status: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  history: any;
  created_at: string;
}

@Injectable({
  providedIn: 'root',
})
export class DbService {
  private readonly base = environment.backendUrl + '/db';

  constructor(private http: HttpClient) {}

  // ----------------------------------------------------------------------- //
  // repositories
  // ----------------------------------------------------------------------- //

  /** 1. `GET /db/repo` — list the repositories owned by the current user. */
  listRepos(): Promise<RepoOut[]> {
    return lastValueFrom(this.http.get<RepoOut[]>(`${this.base}/repo`));
  }

  /** 2. `GET /db/repo/{repo_id}` — fetch a single owned repository. */
  getRepo(repoId: string): Promise<RepoOut> {
    return lastValueFrom(this.http.get<RepoOut>(`${this.base}/repo/${repoId}`));
  }

  // ----------------------------------------------------------------------- //
  // ui history
  // ----------------------------------------------------------------------- //

  /** 3. `GET /db/uihistory?repo_id=` — list labelled chats in a repo, newest first. */
  listChats(repoId: string): Promise<ChatOut[]> {
    return lastValueFrom(
      this.http.get<ChatOut[]>(`${this.base}/uihistory`, {
        params: { repo_id: repoId },
      })
    );
  }

  /** 4. `GET /db/uihistory/{session_id}` — fetch the UI history for one session. */
  getUiHistory(sessionId: string): Promise<HistoryOut> {
    return lastValueFrom(
      this.http.get<HistoryOut>(`${this.base}/uihistory/${sessionId}`)
    );
  }

  /** 5. `PUT /db/uihistory` — create or replace the UI history for a session. */
  upsertUiHistory(payload: HistoryUpsertIn): Promise<HistoryOut> {
    return lastValueFrom(
      this.http.put<HistoryOut>(`${this.base}/uihistory`, payload)
    );
  }

  // ----------------------------------------------------------------------- //
  // internal (agent) history
  // ----------------------------------------------------------------------- //

  /** 6. `GET /db/internalhistory/{session_id}` — fetch the agent history. */
  getInternalHistory(sessionId: string): Promise<HistoryOut> {
    return lastValueFrom(
      this.http.get<HistoryOut>(`${this.base}/internalhistory/${sessionId}`)
    );
  }

  /** 7. `PUT /db/internalhistory` — create or replace the agent history. */
  upsertInternalHistory(payload: HistoryUpsertIn): Promise<HistoryOut> {
    return lastValueFrom(
      this.http.put<HistoryOut>(`${this.base}/internalhistory`, payload)
    );
  }

  // ----------------------------------------------------------------------- //
  // usdm sessions / state histories
  // ----------------------------------------------------------------------- //

  /** 8. `GET /db/usdm/sessions` — list the user's USDM authoring sessions. */
  listUsdmSessions(): Promise<USDMSessionOut[]> {
    return lastValueFrom(
      this.http.get<USDMSessionOut[]>(`${this.base}/usdm/sessions`)
    );
  }

  /** 9. `PATCH /db/usdm/sessions/{session_id}` — patch a USDM session. */
  updateUsdmSession(
    sessionId: string,
    payload: USDMSessionUpdateIn
  ): Promise<USDMSessionOut> {
    return lastValueFrom(
      this.http.patch<USDMSessionOut>(
        `${this.base}/usdm/sessions/${sessionId}`,
        payload
      )
    );
  }

  /** 10. `GET /db/usdm/state-histories/{session_id}` — list per-state history rows. */
  listUsdmStateHistories(sessionId: string): Promise<USDMStateHistoryOut[]> {
    return lastValueFrom(
      this.http.get<USDMStateHistoryOut[]>(
        `${this.base}/usdm/state-histories/${sessionId}`
      )
    );
  }
}
